'use client';
import { useState, useEffect, useCallback } from 'react';
import { parse } from 'date-fns';
import { tz } from '@date-fns/tz';

export type CountdownTime = {
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
};

const MILLISECONDS_IN_SECOND = 1000;
const SECONDS_IN_MINUTE = 60;
const MINUTES_IN_HOUR = 60;
const DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss';
const TIMEZONE_SGT = 'Asia/Singapore';

/**
 * Custom hook for countdown timer with timezone support
 * @param targetDate - The target date and time for countdown in format 'yyyy-MM-dd HH:mm:ss'
 * @param timezone - The timezone of the target date (defaults to 'Asia/Singapore')
 * @returns CountdownTime object with hours, minutes, seconds, and expiration status
 */
export const useCountdown = (targetDate: string, timezone: string = TIMEZONE_SGT): CountdownTime => {
  const calculateTimeLeft = useCallback((): CountdownTime => {
    const targetDateTime = parse(targetDate, DATE_FORMAT, new Date(), { in: tz(timezone) });
    const difference = targetDateTime.getTime() - new Date().getTime();

    if (difference <= 0) {
      return {
        hours: 0,
        minutes: 0,
        seconds: 0,
        isExpired: true,
      };
    }

    const totalSeconds = Math.floor(difference / MILLISECONDS_IN_SECOND);
    const totalMinutes = Math.floor(totalSeconds / SECONDS_IN_MINUTE);
    const totalHours = Math.floor(totalMinutes / MINUTES_IN_HOUR);

    return {
      hours: totalHours,
      minutes: totalMinutes % MINUTES_IN_HOUR,
      seconds: totalSeconds % SECONDS_IN_MINUTE,
      isExpired: false,
    };
  }, [targetDate, timezone]);

  const [timeLeft, setTimeLeft] = useState<CountdownTime>(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft.isExpired) {
        clearInterval(timer);
      }
    }, MILLISECONDS_IN_SECOND);

    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  return timeLeft;
};
